import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Granularity of the bookable time slots offered in the UI. Display-only —
 * appointments themselves can still start/end at any minute (e.g. existing
 * bookings, or durations that aren't a multiple of this).
 */
export const SLOT_INTERVAL_MINUTES = 15

/**
 * Round a time up to the next SLOT_INTERVAL_MINUTES boundary so slots always
 * land on the clock grid (:00/:15/:30/:45) even when business hours start on
 * an odd minute.
 */
const snapUpToSlotGrid = (time) => {
	const remainder = time.minute() % SLOT_INTERVAL_MINUTES
	return remainder === 0 ? time : time.add(SLOT_INTERVAL_MINUTES - remainder, 'minute')
}

/**
 * Generate time slots based on merchant hours for a specific day
 * @param {number} dayOfWeek - Day of the week (0 = Sunday, 1 = Monday, etc.)
 * @param {Object} merchantHours - Merchant's weekly hours object
 * @param {boolean} isMerchantMode - If true, generate all-day slots (merchant owner booking)
 * @returns {string[]} Array of time slots in HH:mm format
 */
export const generateTimeSlots = (dayOfWeek, merchantHours, isMerchantMode = false) => {
	// For merchant owners, generate all-day slots (6 AM to 10 PM)
	if (isMerchantMode) {
		const slots = []
		const start = dayjs(`2000-01-01 06:00`)
		const end = dayjs(`2000-01-01 22:00`)

		let current = snapUpToSlotGrid(start)
		while (current.isBefore(end)) {
			slots.push(current.format('HH:mm'))
			current = current.add(SLOT_INTERVAL_MINUTES, 'minute')
		}

		return slots.sort()
	}

	// Regular customer booking - use business hours
	const dayKey = String(dayOfWeek)
	const intervals = merchantHours[dayKey] || []

	if (intervals.length === 0) return []

	const slots = []
	intervals.forEach(interval => {
		const start = dayjs(`2000-01-01 ${interval.start}`)
		const end = dayjs(`2000-01-01 ${interval.end}`)

		let current = snapUpToSlotGrid(start)
		while (current.isBefore(end)) {
			slots.push(current.format('HH:mm'))
			current = current.add(SLOT_INTERVAL_MINUTES, 'minute')
		}
	})

	return slots.sort()
}

/**
 * Check if date is closed due to recurring closed dates
 * @param {dayjs.Dayjs|string} date - Date to check
 * @param {string[]} closedDates - Array of closed dates in 'MM-DD' format
 * @returns {boolean} True if the date is closed
 */
export const isDateClosed = (date, closedDates) => {
	if (!closedDates || closedDates.length === 0) return false
	const mmdd = dayjs(date).format('MM-DD')
	return closedDates.includes(mmdd)
}

/**
 * Calculate employee skill count for resource allocation
 * @param {Object} employee - Employee object with services array
 * @param {Object[]} allServices - Array of all available services
 * @returns {number} Number of services the employee can perform
 */
export const getEmployeeSkillCount = (employee, allServices) => {
	if (!employee.services || employee.services.length === 0) {
		// Universal employee - can do all services, so skill count = total services
		return allServices.length || 999
	}
	return employee.services.length
}

/**
 * Check if employee can perform a specific service
 * @param {Object} employee - Employee object with services array
 * @param {string} serviceId - Service ID to check
 * @returns {boolean} True if employee can perform the service
 */
export const canEmployeePerformService = (employee, serviceId) => {
	if (!employee.services || employee.services.length === 0) {
		return true // Universal employee
	}
	return employee.services.includes(serviceId)
}

/**
 * Resource allocation algorithm: simulate optimal employee assignment using pre-loaded appointments
 * @param {string} requestedServiceId - ID of the service being requested
 * @param {string} startTs - Start timestamp (ISO string)
 * @param {string} endTs - End timestamp (ISO string)
 * @param {Object[]} allDayAppointments - Array of existing appointments for the day
 * @param {Object[]} employees - Array of available employees
 * @param {Object[]} allServices - Array of all available services
 * @param {Object} merchant - Merchant object
 * @returns {number} Number of available capacity for the requested service
 */
export const simulateResourceAllocation = (
	requestedServiceId,
	startTs,
	endTs,
	allDayAppointments,
	employees,
	allServices,
	merchant
) => {
	if (!merchant || employees.length === 0) return 0

	const overlappingAppointments = allDayAppointments.filter(appointment => {
		const apptStart = appointment.start_ts
		const apptEnd = appointment.end_ts
		return startTs < apptEnd && endTs > apptStart
	})

	let availableEmployees = [...employees]

	if (overlappingAppointments.length === 0) {
		return availableEmployees.filter(emp =>
			canEmployeePerformService(emp, requestedServiceId)
		).length
	}

	// Greedy assignment for existing appointments
	for (const appointment of overlappingAppointments) {
		let bestEmployee = null
		let minSkillCount = Infinity

		for (const employee of availableEmployees) {
			if (canEmployeePerformService(employee, appointment.service_id)) {
				const skillCount = getEmployeeSkillCount(employee, allServices)
				if (skillCount < minSkillCount) {
					minSkillCount = skillCount
					bestEmployee = employee
				}
			}
		}

		if (bestEmployee) {
			availableEmployees = availableEmployees.filter(emp => emp.id !== bestEmployee.id)
		}
	}

	return availableEmployees.filter(emp =>
		canEmployeePerformService(emp, requestedServiceId)
	).length
}

/**
 * Check which employees are available for a specific time slot
 * @param {Object} supabase - Supabase client
 * @param {string} merchantId - Merchant ID
 * @param {string} serviceId - Service ID
 * @param {string} startTs - Start timestamp (ISO string)
 * @param {string} endTs - End timestamp (ISO string)
 * @param {Object[]} employees - Array of all employees
 * @returns {Promise<Object[]>} Array of available employees
 */
export const getAvailableEmployeesForTimeSlot = async (
	supabase,
	merchantId,
	serviceId,
	startTs,
	endTs,
	employees
) => {
	try {
		// Fetch existing appointments that overlap with the selected time slot
		const { data: overlappingAppointments, error: appointmentsError } = await supabase
			.from('appointments')
			.select('employee_id')
			.eq('merchant_id', merchantId)
			.eq('status', 'booked')
			.lt('start_ts', endTs)
			.gt('end_ts', startTs)

		if (appointmentsError) {
			console.error('Error loading overlapping appointments:', appointmentsError)
			return []
		}

		// Get list of busy employee IDs
		const busyEmployeeIds = new Set(
			overlappingAppointments
				?.map(apt => apt.employee_id)
				?.filter(id => id !== null) || []
		)

		// Filter employees who can perform the service AND are not busy
		const available = employees.filter(emp =>
			canEmployeePerformService(emp, serviceId) &&
			!busyEmployeeIds.has(emp.id)
		)

		return available
	} catch (error) {
		console.error('Error checking employee availability:', error)
		return []
	}
}

/**
 * Check if a specific employee is busy during a time slot
 * @param {Object} supabase - Supabase client
 * @param {string} merchantId - Merchant ID
 * @param {string} employeeId - Employee ID
 * @param {string} startTs - Start timestamp (ISO string)
 * @param {string} endTs - End timestamp (ISO string)
 * @returns {Promise<boolean>} True if employee is busy
 */
export const isEmployeeBusyDuringSlot = async (
	supabase,
	merchantId,
	employeeId,
	startTs,
	endTs
) => {
	try {
		const { data: appointments, error } = await supabase
			.from('appointments')
			.select('id')
			.eq('merchant_id', merchantId)
			.eq('employee_id', employeeId)
			.eq('status', 'booked')
			.lt('start_ts', endTs)
			.gt('end_ts', startTs)
			.limit(1)

		if (error) {
			console.error('Error checking employee availability:', error)
			return true // Assume busy if there's an error
		}

		return appointments && appointments.length > 0
	} catch (error) {
		console.error('Error checking employee availability:', error)
		return true // Assume busy if there's an error
	}
}

/**
 * Convert local date and time to UTC timestamps
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:mm format
 * @param {string} timezone - Timezone string (e.g., 'America/New_York')
 * @param {number} durationMinutes - Duration in minutes
 * @returns {Object} Object with utcStart and utcEnd timestamps
 */
export const convertToUtcTimestamps = (date, time, timezone, durationMinutes) => {
	const localDateTime = date + ' ' + time
	const utcStart = dayjs.tz(localDateTime, timezone).utc()
	const utcEnd = utcStart.add(durationMinutes, 'minute')

	return {
		utcStart: utcStart.toISOString(),
		utcEnd: utcEnd.toISOString()
	}
}

/**
 * Validate if a date should be disabled in date picker
 * @param {dayjs.Dayjs} date - Date to validate
 * @param {Object} merchant - Merchant object with timezone, weekly_hours, closed_recurring_dates
 * @param {boolean} isMerchantMode - If true, allow booking on any day (merchant owner booking)
 * @returns {boolean} True if date should be disabled
 */
export const shouldDisableDate = (date, merchant, isMerchantMode = false) => {
	if (!merchant) return true

	// For merchant owners, allow past dates
	// For regular customers, disable past dates
	if (!isMerchantMode && date.isBefore(dayjs(), 'day')) return true

	// Check if date is in closed recurring dates
	if (isDateClosed(date, merchant.closed_recurring_dates)) return true

	// For merchant owners, allow booking on any day (skip business hours check)
	if (isMerchantMode) return false

	// For regular customers, check if merchant has any hours on this day
	const dayOfWeek = date.day()
	const dayKey = String(dayOfWeek)
	const intervals = merchant.weekly_hours[dayKey] || []

	return intervals.length === 0
}


export const getAppointmentStatusDisplay = (status) => {
	console.log('status', status)
	if (status === 'confirmed') return { label: 'Upcoming', color: '#1447E6', bgcolor: '#DBEAFE' }
	if (status === 'completed') return { label: 'Completed', color: '#065F46', bgcolor: '#D1FAE5' }
	if (status === 'noShow') return { label: 'No Show', color: '#991B1B', bgcolor: '#FEE2E2' }
	return { label: status, color: '#1447E6', bgcolor: '#DBEAFE' }
}

/**
 * Minimum interval for duration adjustments (5 minutes)
 */
export const MIN_DURATION_INTERVAL = 5

/**
 * Calculate proportional duration changes for multiple appointments
 * 
 * This algorithm distributes a new total duration proportionally across appointments,
 * ensuring each appointment maintains at least 5 minutes and durations are rounded
 * to 5-minute intervals.
 * 
 * Example: Appointment A (30 min) + Appointment B (15 min) = 45 min total
 *          New target: 30 min
 *          Result: A = 20 min (proportional), B = 10 min (proportional)
 * 
 * @param {Array} appointments - Array of appointment objects with duration info
 *        Each object should have: { duration: number } (in minutes)
 * @param {number} newTotalDuration - The desired new total duration in minutes
 * @returns {Array} Array of objects with original and new durations
 *          Each object: { originalDuration, newDuration, index }
 */
export const calculateProportionalDurations = (appointments, newTotalDuration) => {
	if (!appointments || appointments.length === 0) {
		return []
	}

	// Calculate current total duration
	const currentTotal = appointments.reduce((sum, apt) => sum + (apt.duration || 0), 0)

	// If current total is 0, distribute evenly
	if (currentTotal === 0) {
		const evenDuration = Math.max(MIN_DURATION_INTERVAL, Math.floor(newTotalDuration / appointments.length / MIN_DURATION_INTERVAL) * MIN_DURATION_INTERVAL)
		return appointments.map((apt, index) => ({
			originalDuration: apt.duration || 0,
			newDuration: evenDuration,
			index
		}))
	}

	// Calculate the ratio for proportional distribution
	const ratio = newTotalDuration / currentTotal

	// First pass: Calculate raw proportional durations
	const rawDurations = appointments.map((apt, index) => {
		const originalDuration = apt.duration || 0
		const rawNewDuration = originalDuration * ratio
		return {
			originalDuration,
			rawNewDuration,
			index
		}
	})

	// Second pass: Round to 5-minute intervals while respecting minimum
	let roundedResults = rawDurations.map(item => {
		let rounded = Math.round(item.rawNewDuration / MIN_DURATION_INTERVAL) * MIN_DURATION_INTERVAL
		// Ensure minimum duration of 5 minutes
		rounded = Math.max(MIN_DURATION_INTERVAL, rounded)
		return {
			originalDuration: item.originalDuration,
			newDuration: rounded,
			index: item.index
		}
	})

	// Third pass: Adjust for rounding errors to match target total
	let roundedTotal = roundedResults.reduce((sum, r) => sum + r.newDuration, 0)
	let difference = newTotalDuration - roundedTotal

	// Distribute the difference across appointments (prioritize larger appointments)
	// Sort by original duration descending to adjust larger appointments first
	const sortedBySize = [...roundedResults].sort((a, b) => b.originalDuration - a.originalDuration)

	let adjustmentIndex = 0
	while (difference !== 0 && adjustmentIndex < sortedBySize.length * 2) {
		const currentItem = sortedBySize[adjustmentIndex % sortedBySize.length]
		const adjustment = difference > 0 ? MIN_DURATION_INTERVAL : -MIN_DURATION_INTERVAL

		// Only adjust if it won't go below minimum
		if (currentItem.newDuration + adjustment >= MIN_DURATION_INTERVAL) {
			currentItem.newDuration += adjustment
			difference -= adjustment
		}

		adjustmentIndex++

		// Prevent infinite loop
		if (adjustmentIndex > sortedBySize.length * 10) {
			break
		}
	}

	// Return results in original order
	return roundedResults.sort((a, b) => a.index - b.index)
}

/**
 * Get duration for a single appointment
 * 
 * Priority order:
 * 1. If _actualDuration is set (stored duration from DB), use that
 * 2. If start_ts and end_ts are available, calculate from timestamps
 * 3. Otherwise, calculate from service + add-ons durations
 * 
 * @param {Object} appointment - Appointment object with service and addOns
 * @returns {number} Total duration in minutes
 */
export const getAppointmentDuration = (appointment) => {
	if (!appointment) return 0

	// Priority 1: Use stored actual duration if available
	if (appointment._actualDuration !== undefined && appointment._actualDuration > 0) {
		return appointment._actualDuration
	}

	// Priority 2: Calculate from timestamps if available (for existing appointments)
	if (appointment.start_ts && appointment.end_ts) {
		const start = dayjs(appointment.start_ts)
		const end = dayjs(appointment.end_ts)
		const durationFromTimestamps = end.diff(start, 'minute')
		if (durationFromTimestamps > 0) {
			return durationFromTimestamps
		}
	}

	// Priority 3: Calculate from service + add-ons (for new appointments)
	const serviceDuration = appointment.service?.duration_minutes || 0
	const addOnsDuration = (appointment.addOns || []).reduce(
		(sum, addon) => sum + (addon.duration_minutes || 0),
		0
	)

	return serviceDuration + addOnsDuration
}

/**
 * Apply new durations to appointments and recalculate start/end times
 * 
 * @param {Array} appointments - Array of appointment objects
 * @param {Array} newDurations - Array of { index, newDuration } from calculateProportionalDurations
 * @param {string} startTime - Start time in HH:mm format
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} timezone - Timezone string
 * @returns {Array} Updated appointments with adjusted durations and times
 */
export const applyDurationChangesToAppointments = (appointments, newDurations, startTime, date, timezone) => {
	if (!appointments || appointments.length === 0 || !newDurations || newDurations.length === 0) {
		return appointments
	}

	// Create a map of new durations by index
	const durationMap = new Map(newDurations.map(d => [d.index, d.newDuration]))

	// Track current time position for sequential appointments
	let currentStartTime = dayjs.tz(`${date} ${startTime}`, timezone)

	return appointments.map((apt, index) => {
		const newDuration = durationMap.get(index)

		if (newDuration === undefined) {
			return apt
		}

		// Calculate the duration ratio to distribute across service and add-ons
		const originalDuration = getAppointmentDuration(apt)
		const durationRatio = originalDuration > 0 ? newDuration / originalDuration : 1

		// Update service duration proportionally
		const originalServiceDuration = apt.service?.duration_minutes || 0
		const newServiceDuration = Math.max(
			MIN_DURATION_INTERVAL,
			Math.round((originalServiceDuration * durationRatio) / MIN_DURATION_INTERVAL) * MIN_DURATION_INTERVAL
		)

		// Update add-ons durations proportionally (keeping total correct)
		const newAddOns = (apt.addOns || []).map(addon => {
			const originalAddonDuration = addon.duration_minutes || 0
			const newAddonDuration = Math.round((originalAddonDuration * durationRatio) / MIN_DURATION_INTERVAL) * MIN_DURATION_INTERVAL
			return {
				...addon,
				duration_minutes: newAddonDuration
			}
		})

		// Recalculate actual new duration after rounding
		const actualNewDuration = newServiceDuration + newAddOns.reduce((sum, a) => sum + a.duration_minutes, 0)

		// Calculate new timestamps
		const aptStartTime = currentStartTime
		const aptEndTime = currentStartTime.add(actualNewDuration || newDuration, 'minute')

		// Update current time for next appointment
		currentStartTime = aptEndTime

		// Calculate new timestamps as ISO strings
		const newStartTs = aptStartTime.utc().toISOString()
		const newEndTs = aptEndTime.utc().toISOString()

		return {
			...apt,
			service: apt.service ? {
				...apt.service,
				duration_minutes: newServiceDuration
			} : apt.service,
			addOns: newAddOns,
			// Update the actual timestamps (used by getAppointmentDuration)
			start_ts: newStartTs,
			end_ts: newEndTs,
			// Also store as _calculated for explicit reference
			_calculatedStartTs: newStartTs,
			_calculatedEndTs: newEndTs,
			// Store the actual duration for quick access
			_actualDuration: actualNewDuration || newDuration
		}
	})
}
/**
 * Price of record for an appointment's service, in cents.
 * Frozen on the row at booking (appointments.service_price, migration 37) so
 * catalog price changes never alter existing or completed appointments. The
 * live catalog row — joined as `service_types` or fetched separately and
 * passed as `service` — is only a fallback for rows that predate the column.
 */
export const getServicePrice = (appointment, service = null) =>
	appointment?.service_price ?? service?.price ?? appointment?.service_types?.price ?? 0
